import { useCallback, useEffect, useState } from 'react';
import { useBatchId } from '../../../hooks/useBatchId';
import { useTranslation } from 'react-i18next';
import {
	CENTRAL_TEAM_APPLICATIONS,
	FUNCTIONARY_HOME,
} from '../../../shared/Routes';
import { Button, Spin, Table, TableColumnsType, Typography } from 'antd';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import Icon from '../../../components/ui-components/Icon';
import { UndoOutlined } from '@ant-design/icons';
import { useCentralTeamDashboard } from './useCentralTeamDashboard';
import { FilterCentralTeamDashboard } from './FilterCentralTeamDashboard';
import { useScreeningDashboard } from '../DashboardPage/useScreeningDashboard';
import { CandidateListData } from '../../../shared/VolunteerApplicationServiceTypes';
import ProposerIcon from '../../../assets/images/proposer-icon.svg';
import ApprovedCandidates from '../../../assets/images/approved-candidates.svg';
import NotGoingForward from '../../../assets/images/not-going-forward.svg';
import { CTDCategories } from '../../../shared/Constants';
import { PreceptorApplicationCentralTeamDashboardResponse } from '../../../shared/ScreeningServiceTypes';

const { Text } = Typography;

interface CTDRow {
	key: number;
	categoryKey: string;
	count: number;
}

export const CentralTeamDashboard = () => {
	const { t, i18n } = useTranslation();
	const lengthyLanguages = ['ta', 'ma'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const { batchId, navigateToRoute, navigateWithCTDCategory } = useBatchId();
	const { CTDDetail, CTDFilter, onCTDFilterChange } = useCentralTeamDashboard();
	const { adminDashboardResponse, onDashboardFilterChange, loading } =
		useScreeningDashboard();
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [isFilterApplied, setIsFilterApplied] = useState(false);
	const [dashboardData, setDashboardData] = useState<CandidateListData[]>([]);

	const getDashboardData = useCallback(() => {
		if (adminDashboardResponse) {
			const refinedResponseData: CandidateListData[] = [
				{
					id: 'overallCandidateApplications',
					count: adminDashboardResponse.overallCandidateApplication || 0,
					description: t('overallCandidateApplications'),
					imageSrc: ProposerIcon,
				},
				{
					id: 'approvedCandidates',
					count: adminDashboardResponse.approvedCandidates || 0,
					description: t('approvedCandidates'),
					imageSrc: ApprovedCandidates,
				},
				{
					id: 'notGoingForwardCandidates',
					count: adminDashboardResponse.notGoingForwardCandidates || 0,
					description: t('notGoingForwardCandidates'),
					imageSrc: NotGoingForward,
				},
			];
			setDashboardData(refinedResponseData);
		}
	}, [adminDashboardResponse, t]);

	useEffect(() => {
		void getDashboardData();
	}, [getDashboardData]);

	useEffect(() => {
		if (CTDFilter.regionId || CTDFilter.zoneId?.length) {
			setIsFilterApplied(true);
		} else {
			setIsFilterApplied(false);
		}
	}, [CTDFilter]);

	const CTDDataSource: CTDRow[] | undefined =
		CTDDetail &&
		Object.values(CTDCategories).map((categoryKey, index) => ({
			key: index,
			categoryKey,
			count:
				CTDDetail[
					categoryKey as keyof PreceptorApplicationCentralTeamDashboardResponse
				] || 0,
		}));

	const handleViewCategoryClick = useCallback(
		(record: CTDRow) => {
			navigateWithCTDCategory(CENTRAL_TEAM_APPLICATIONS, record.categoryKey, {
				state: {
					CTDFilter,
				},
			});
		},
		[navigateWithCTDCategory, CTDFilter]
	);

	const CTDTableColumns: TableColumnsType<CTDRow> = [
		{
			title: t('category'),
			dataIndex: 'categoryKey',
			key: 'categoryKey',
			render: (key: string) => t(key),
		},
		{
			title: t('count'),
			dataIndex: 'count',
			key: 'count',
		},
		{
			title: t('action'),
			key: 'action',
			width: 200,
			render: (__, record) => (
				<Button
					block
					className="border border-primary-blue text-primary-blue font-bold mt-2"
					disabled={record.count === 0}
					onClick={() => handleViewCategoryClick(record)}
				>
					{t('view')}
				</Button>
			),
		},
	];

	const handleBackClick = useCallback(() => {
		navigateToRoute(FUNCTIONARY_HOME);
	}, [navigateToRoute]);

	const handleFilterPopup = () => {
		setIsFilterModalOpen(true);
	};

	const handleFilterModalClose = () => {
		setIsFilterModalOpen(false);
	};

	const handleResetFilters = useCallback(() => {
		onCTDFilterChange({ batchId: [batchId] });
		onDashboardFilterChange({});
		setIsFilterApplied(false);
	}, [batchId, onCTDFilterChange, onDashboardFilterChange]);

	return (
		<>
			<div className="md:hidden flex justify-center items-center pt-3 pb-1 relative">
				<Text className="font-sans font-bold text-lg mb-1">
					{t('centralTeamDashboard')}
				</Text>
			</div>
			<div className="flex justify-between items-center pt-1 pb-3 px-5 md:px-5 md:py-5 relative">
				{/* Back Button - Desktop */}
				<Button
					type="primary"
					onClick={handleBackClick}
					className="hidden md:block font-bold text-sm"
				>
					<span className="flex gap-2">
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</span>
				</Button>

				{/* Back Button + Title - Mobile */}
				<div className="block sm:hidden">
					<a
						onClick={handleBackClick}
						className="relative top:0 md:top-[3px] mr-2 font-bold text-sm"
					>
						<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
					</a>
				</div>

				{/* Centered Title */}
				<Text className="hidden md:block font-sans font-bold text-xl mb-1 absolute left-1/2 transform -translate-x-1/2">
					{t('centralTeamDashboard')}
				</Text>

				{/* Filter Button - Desktop */}
				<div
					className={`flex ${
						isLengthyLanguage ? 'flex-col-reverse gap-2' : 'flex-row'
					}`}
				>
					{isFilterApplied ? (
						<Button
							className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
					<Button
						className={`hidden md:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterApplied ? t('filterApplied') : t('filter')}
					</Button>
				</div>

				{/* Filter Icon - Mobile */}
				<Button
					className="block sm:hidden"
					variant="outlined"
					onClick={handleFilterPopup}
				>
					<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
				</Button>
			</div>
			{loading ? (
				<div className="flex justify-center items-center min-h-[300px]">
					<Spin size="small" />
				</div>
			) : (
				<>
					<div className="flex flex-wrap gap-4 pt-2 pb-5 px-5 md:py-5 md:px-5">
						{dashboardData.map((data, index) => (
							<div
								key={index}
								className="flex-1 basis-[calc(50%-0.5rem)] max-w-[calc(50%-0.5rem)] lg:basis-[calc(25%-0.75rem)] lg:max-w-[calc(25%-0.75rem)]"
							>
								<div
									className={`bg-card-bg md:bg-white rounded-lg border-solid px-2 md:px-4 py-2 h-full cursor-pointer border-gray-200`}
								>
									<div className="flex gap-3 md:gap-6 items-center min-h-[100px]">
										<div className="flex-shrink-0">
											<img
												src={data.imageSrc}
												className="w-[38px] md:w-[50px] h-[38px] md:h-[50px]"
											/>
										</div>

										<div className="flex flex-col justify-center">
											<Text className="text-sm md:text-lg leading-normal font-bold m-0">
												{data.count}
											</Text>
											<Text className="text-[12px] md:text-[16px] text-gray-600 m-0">
												{data.description}
											</Text>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
					<div className="hidden md:block px-4">
						<div className="border rounded-xl shadow-sm bg-white">
							<Table
								dataSource={CTDDataSource}
								columns={CTDTableColumns}
								pagination={false}
								className="border border-solid border-gray-200 rounded-md"
								size="middle"
							/>
						</div>
					</div>
					<div className="block md:hidden px-4 space-y-4">
						{CTDDataSource?.map((item) => (
							<div
								key={item.key}
								className="bg-bground-blue border border-solid border-gray-200 rounded-md shadow-sm p-4"
							>
								<div className="flex justify-between mb-2">
									<span className="text-gray-650 font-sans text-sm">
										{t('category')}
									</span>
									<span className="text-gray-650 font-sans text-sm">
										{t('count')}
									</span>
								</div>
								<div className="flex justify-between items-center font-sans text-sm text-gray-650 mb-2">
									<span className="max-w-[80%]">{t(item.categoryKey)}</span>
									<span className="text-right font-bold">
										{item.count.toLocaleString()}
									</span>
								</div>
								<Button
									block
									className="border border-primary-blue text-primary-blue font-bold mt-2"
									disabled={item.count === 0}
									onClick={() => handleViewCategoryClick(item)}
								>
									{t('view')}
								</Button>
							</div>
						))}
					</div>
				</>
			)}
			<FilterCentralTeamDashboard
				open={isFilterModalOpen}
				onClose={handleFilterModalClose}
				filterValue={CTDFilter}
				onFilterChange={onCTDFilterChange}
				onScreeningDashboardFilterChange={onDashboardFilterChange}
			/>
		</>
	);
};
